import { SyntheticEvent, useState } from 'react';

// material-ui
import { AlertProps, Button, Divider, Fade, Grid, Grow, Slide, SlideProps, SnackbarOrigin, Typography, useMediaQuery } from '@mui/material';
import MuiSnackbar from '@mui/material/Snackbar';

import { KeyedObject } from 'types/root';
import { useTheme } from '@mui/material/styles';
// import { closeSnackbar } from 'store/reducers/snackbar';

// animation function
function TransitionSlideLeft(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

function TransitionSlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

function TransitionSlideRight(props: SlideProps) {
  return <Slide {...props} direction="right" />;
}

function TransitionSlideDown(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

function GrowTransition(props: SlideProps) {
  return <Grow {...props} />;
}

// animation options
const animation: KeyedObject = {
  SlideLeft: TransitionSlideLeft,
  SlideUp: TransitionSlideUp,
  SlideRight: TransitionSlideRight,
  SlideDown: TransitionSlideDown,
  Grow: GrowTransition,
  Fade
};

export interface CustomSnackbarProps {
  action: boolean;
  open: boolean;
  message: string | JSX.Element;
  subMessage?: string | JSX.Element;
  buttonName: string;
  handleButton: () => void;
  anchorOrigin: SnackbarOrigin;
  alert: AlertProps;
  transition: string;
  duration: number;
  isComponentMessage?: boolean;
}

// ==============================|| SNACKBAR ||============================== //

const CustomSnackbar = ({
  anchorOrigin,
  alert,
  message,
  buttonName,
  handleButton,
  subMessage,
  open,
  transition,
  duration,
  isComponentMessage = false
}: CustomSnackbarProps) => {
  const theme = useTheme();
  const [internalOpen, setInternalOpen] = useState(open);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
    console.log('handleClose: ', event, reason);
    if (reason === 'clickaway') {
      return;
    }
    console.log('internal close');
    setInternalOpen(false);
  };

  return (
    <>
      <MuiSnackbar
        TransitionComponent={animation[transition]}
        anchorOrigin={anchorOrigin}
        open={internalOpen}
        autoHideDuration={duration === -1 ? null : duration}
        onClose={handleClose}
      >
        <Grid
          container
          sx={{ minWidth: isMobile ? '70vw' : '580px', maxWidth: isMobile ? '90vw' : '80vw', minHeight: '48px', borderRadius: '50px' }}
          bgcolor={theme.palette.mode === 'light' ? '#3b3d4c' : theme.palette.grey['A400']}
          display={'flex'}
        >
          <Grid item xs={isMobile ? 9 : subMessage ? 4 : 8} display={'flex'} justifyContent={'left'}>
            {isComponentMessage && <>{message}</>}
            {!isComponentMessage && (
              <Typography color={theme.palette.common.white} sx={{ lineHeight: '48px', verticalAlign: 'middle', pl: isMobile ? 3 : 4 }}>
                {message}
              </Typography>
            )}
          </Grid>
          {subMessage && (
            <Grid item xs={4} display={'flex'} justifyContent={'left'} sx={{ minHeight: '48px', margin: 'auto' }}>
              <Typography fontSize={12}>{subMessage}</Typography>
            </Grid>
          )}
          {!isMobile && (
            <Grid item xs={4} display={'flex'} justifyContent={'right'} sx={{ pr: 3 }}>
              {buttonName && (
                <>
                  <Button
                    sx={{ marginY: 'auto', minWidth: '100px' }}
                    size="small"
                    aria-label="close"
                    variant="contained"
                    color={alert.color}
                    onClick={handleButton}
                  >
                    {buttonName || 'Action'}
                  </Button>
                  <Divider sx={{ ml: 4, mr: 3, marginY: 'auto', height: '60%', lineHeight: '48px' }} orientation={'vertical'} />
                </>
              )}
              <Button
                sx={{
                  marginTop: 'auto',
                  marginBottom: 'auto',
                  '&.MuiButton-root': {
                    '&:hover': {
                      backgroundColor: `${theme.palette.mode === 'light' ? '#3b3d4c' : theme.palette.grey['A400']}`,
                      color: `${theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black}`
                    }
                  }
                }}
                size="small"
                aria-label="close"
                variant="text"
                color={'secondary'}
                onClick={handleClose}
                disableElevation={true}
              >
                Close
              </Button>
            </Grid>
          )}
          {isMobile && (
            <Grid item xs={3} display={'flex'} justifyContent={'right'} sx={{ pr: 3 }}>
              {buttonName && (
                <>
                  <Button
                    sx={{ marginY: 'auto', minWidth: '60px', maxWidth: '100px' }}
                    size="small"
                    aria-label="close"
                    variant="contained"
                    color={alert.color}
                    onClick={handleButton}
                  >
                    {buttonName || 'Action'}
                  </Button>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </MuiSnackbar>
    </>
  );
};

export default CustomSnackbar;
